import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Package, CheckCircle, AlertCircle, Loader2, ScanLine, Plus, Eye, EyeOff, Calendar } from 'lucide-react';
import { stcTransService } from '../../../../../services/stcTransService';

export interface BatchCountResult {
  batchNo: string;
  expiryDate?: string;
  quantity: number;
  /** 'confirmed' = telt (qty fra input), 'missing' = ikke funnet (qty=0), 'new' = ny batch */
  status: 'confirmed' | 'missing' | 'new';
}

interface BatchCountingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: (results: BatchCountResult[]) => void;
  itemNo: string;
  itemDescription: string;
  binCode: string;
  warehouseId: string;
  itemType: number; // 4 = batch med holdbarhet, 5 = batch med lagerstyring
}

interface ExpectedBatch {
  batchNo: string;
  expiryDate: string | null;
  currentQty: number;
  countedQty: string; // string for input binding
  confirmed: boolean;
}

interface NewBatch {
  batchNo: string;
  expiryDate: string;
  quantity: string;
}

export const BatchCountingModal: React.FC<BatchCountingModalProps> = ({
  isOpen,
  onClose,
  onComplete,
  itemNo,
  itemDescription,
  binCode,
  warehouseId,
  itemType
}) => {
  const [expectedBatches, setExpectedBatches] = useState<ExpectedBatch[]>([]);
  const [newBatches, setNewBatches] = useState<NewBatch[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAddNew, setShowAddNew] = useState(false);
  const [newBatchNo, setNewBatchNo] = useState('');
  const [newExpiryDate, setNewExpiryDate] = useState('');
  const [newQuantity, setNewQuantity] = useState('');
  const scanInputRef = useRef<HTMLInputElement>(null);
  const addBatchInputRef = useRef<HTMLInputElement>(null);
  const prevIsOpenRef = useRef(false);

  // Reset og last data ved åpning
  useEffect(() => {
    if (isOpen && !prevIsOpenRef.current) {
      setExpectedBatches([]);
      setNewBatches([]);
      setError(null);
      setShowAddNew(false);
      setNewBatchNo('');
      setNewExpiryDate('');
      setNewQuantity('');
      loadExpectedBatches();
    }
    prevIsOpenRef.current = isOpen;
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fokus scan-input
  useEffect(() => {
    if (isOpen && !loading) {
      setTimeout(() => scanInputRef.current?.focus(), 100);
    }
  }, [isOpen, loading]);

  // Fokus add-input når den vises
  useEffect(() => {
    if (showAddNew) {
      setTimeout(() => addBatchInputRef.current?.focus(), 100);
    }
  }, [showAddNew]);

  const loadExpectedBatches = async () => {
    setLoading(true);
    setError(null);
    try {
      const batches = await stcTransService.getBatchesAtBin(itemNo, warehouseId, binCode);
      setExpectedBatches(
        batches.map(b => ({
          batchNo: b.batchNo,
          expiryDate: b.expiryDate,
          currentQty: b.quantity,
          countedQty: String(b.quantity), // Pre-fill med nåværende qty
          confirmed: true
        }))
      );
    } catch (err: any) {
      console.error('Error loading batches:', err);
      setError(err.message || 'Kunne ikke hente batcher');
    } finally {
      setLoading(false);
    }
  };

  const toggleBatch = (batchNo: string) => {
    setExpectedBatches(prev =>
      prev.map(b =>
        b.batchNo === batchNo
          ? { ...b, confirmed: !b.confirmed, countedQty: !b.confirmed ? String(b.currentQty) : '0' }
          : b
      )
    );
  };

  const updateCountedQty = (batchNo: string, value: string) => {
    setExpectedBatches(prev =>
      prev.map(b =>
        b.batchNo === batchNo ? { ...b, countedQty: value, confirmed: true } : b
      )
    );
  };

  // Skann batch i liste -> fokuser kvantitetsinput
  const handleScanInput = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && scanInputRef.current?.value.trim()) {
      e.preventDefault();
      const trimmed = scanInputRef.current.value.trim();

      const match = expectedBatches.find(
        b => b.batchNo.toLowerCase() === trimmed.toLowerCase()
      );
      if (match) {
        // Marker som bekreftet
        setExpectedBatches(prev =>
          prev.map(b =>
            b.batchNo === match.batchNo ? { ...b, confirmed: true } : b
          )
        );
        if (scanInputRef.current) scanInputRef.current.value = '';
        setError(null);
        return;
      }

      // Sjekk om allerede lagt til som ny
      if (newBatches.some(b => b.batchNo.toLowerCase() === trimmed.toLowerCase())) {
        setError(`Batch "${trimmed}" er allerede lagt til`);
        return;
      }

      setError(`"${trimmed}" finnes ikke i forventet beholdning. Bruk "Legg til ny batch" for å registrere.`);
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  const formatExpiryDate = (dateStr: string | null): string => {
    if (!dateStr) return '';
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString('no-NO');
    } catch {
      return dateStr;
    }
  };

  const isExpired = (dateStr: string | null): boolean => {
    if (!dateStr) return false;
    try {
      return new Date(dateStr) < new Date();
    } catch {
      return false;
    }
  };

  const isSoonExpired = (dateStr: string | null): boolean => {
    if (!dateStr) return false;
    try {
      const d = new Date(dateStr);
      const now = new Date();
      const thirtyDays = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
      return d > now && d <= thirtyDays;
    } catch {
      return false;
    }
  };

  // Legg til ny batch
  const addNewBatch = useCallback(() => {
    setError(null);

    // Validering basert på itemType
    if (itemType === 5 && !newBatchNo.trim()) {
      setError('Batchnummer er påkrevd for denne varetypen');
      return;
    }
    if (itemType === 4 && !newExpiryDate) {
      setError('Utløpsdato er påkrevd for denne varetypen');
      return;
    }

    const batchNo = itemType === 4 && !newBatchNo.trim()
      ? newExpiryDate.replace(/-/g, '') // Auto-gen: YYYYMMDD
      : newBatchNo.trim();

    if (!batchNo) {
      setError('Batchnummer er påkrevd');
      return;
    }

    const qty = parseFloat(newQuantity);
    if (isNaN(qty) || qty <= 0) {
      setError('Antall må være større enn 0');
      return;
    }

    // Duplikatsjekk
    if (expectedBatches.some(b => b.batchNo.toLowerCase() === batchNo.toLowerCase())) {
      setError(`Batch "${batchNo}" finnes allerede i forventet beholdning`);
      return;
    }
    if (newBatches.some(b => b.batchNo.toLowerCase() === batchNo.toLowerCase())) {
      setError(`Batch "${batchNo}" er allerede lagt til`);
      return;
    }

    setNewBatches(prev => [...prev, {
      batchNo,
      expiryDate: newExpiryDate,
      quantity: newQuantity
    }]);
    setNewBatchNo('');
    setNewExpiryDate('');
    setNewQuantity('');
    setTimeout(() => addBatchInputRef.current?.focus(), 50);
  }, [newBatchNo, newExpiryDate, newQuantity, itemType, expectedBatches, newBatches]);

  const removeNewBatch = (index: number) => {
    setNewBatches(prev => prev.filter((_, i) => i !== index));
  };

  // Bekreft telling
  const handleConfirm = () => {
    const results: BatchCountResult[] = [];

    // Forventede batcher
    for (const b of expectedBatches) {
      const qty = b.confirmed ? parseFloat(b.countedQty) || 0 : 0;
      results.push({
        batchNo: b.batchNo,
        expiryDate: b.expiryDate || undefined,
        quantity: qty,
        status: b.confirmed ? (qty > 0 ? 'confirmed' : 'missing') : 'missing'
      });
    }

    // Nye batcher
    for (const nb of newBatches) {
      results.push({
        batchNo: nb.batchNo,
        expiryDate: nb.expiryDate || undefined,
        quantity: parseFloat(nb.quantity) || 0,
        status: 'new'
      });
    }

    onComplete(results);
  };

  const stopPropagation = (e: React.SyntheticEvent) => {
    e.stopPropagation();
  };

  if (!isOpen) return null;

  const confirmedCount = expectedBatches.filter(b => b.confirmed).length;
  const missingCount = expectedBatches.filter(b => !b.confirmed).length;
  const newCount = newBatches.length;
  const totalLines = expectedBatches.length + newCount;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-0 sm:p-4"
      onMouseDown={stopPropagation}
      onMouseUp={stopPropagation}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <div
        className="bg-white w-full h-full sm:h-auto sm:rounded-lg sm:max-w-lg sm:max-h-[90vh] flex flex-col overflow-hidden"
        onClick={stopPropagation}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-orange-50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-orange-100">
              <Package className="text-orange-600" size={20} />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Batch-telling
              </h2>
              <p className="text-sm text-gray-500">{itemNo} - {itemDescription}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
          >
            <X size={24} />
          </button>
        </div>

        {/* Info */}
        <div className="px-4 pt-3 pb-2">
          <div className="flex items-center gap-4 text-sm text-gray-600">
            <span>Lokasjon: <span className="font-medium text-gray-900">{binCode}</span></span>
            <span>Batcher: <span className="font-medium text-gray-900">{expectedBatches.length}</span></span>
          </div>
          <p className="text-xs text-gray-400 mt-1">
            Tell antall per batch. Sett antall til 0 eller fjern avmerking for manglende batcher.
          </p>
        </div>

        {/* Skann-input */}
        <div className="px-4 pb-3">
          <div className="relative">
            <ScanLine className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              ref={scanInputRef}
              type="text"
              onKeyDown={handleScanInput}
              placeholder="Skann batchnummer for rask bekreftelse..."
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              autoComplete="off"
            />
          </div>
          {error && (
            <div className="mt-2 flex items-start gap-2 text-sm text-red-600 bg-red-50 rounded-lg p-2">
              <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}
        </div>

        {/* Forventede batcher */}
        <div className="flex-1 overflow-y-auto px-4 pb-2">
          {loading ? (
            <div className="flex items-center justify-center gap-2 text-gray-500 py-8">
              <Loader2 className="animate-spin" size={20} />
              Henter batcher...
            </div>
          ) : expectedBatches.length === 0 && newBatches.length === 0 ? (
            <div className="text-center text-gray-500 py-8">
              <Package size={32} className="mx-auto mb-2 opacity-30" />
              <p className="text-sm">Ingen batcher funnet for denne varen på lokasjonen</p>
              <p className="text-xs mt-1">Bruk "Legg til ny batch" for å registrere nye batcher</p>
            </div>
          ) : (
            <>
              {/* Forventet-liste */}
              {expectedBatches.length > 0 && (
                <div className="space-y-2 mb-3">
                  <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                    Forventet beholdning ({expectedBatches.length})
                  </div>
                  {expectedBatches.map((batch) => (
                    <div
                      key={batch.batchNo}
                      className={`p-3 rounded-lg border transition-all ${
                        batch.confirmed
                          ? 'border-green-400 bg-green-50 ring-1 ring-green-200'
                          : 'border-red-300 bg-red-50 ring-1 ring-red-200'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <button
                          onClick={() => toggleBatch(batch.batchNo)}
                          className="flex items-center gap-3 text-left flex-1"
                        >
                          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                            batch.confirmed ? 'bg-green-500 border-green-500' : 'border-red-300 bg-white'
                          }`}>
                            {batch.confirmed && <CheckCircle className="text-white" size={14} />}
                          </div>
                          <div>
                            <span className="font-mono font-medium text-gray-900">{batch.batchNo}</span>
                            {batch.expiryDate && (
                              <div className="flex items-center gap-1 mt-0.5">
                                <Calendar size={11} className="text-gray-400" />
                                <span className="text-xs text-gray-500">{formatExpiryDate(batch.expiryDate)}</span>
                                {isExpired(batch.expiryDate) && (
                                  <span className="text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded font-medium">Utløpt</span>
                                )}
                                {isSoonExpired(batch.expiryDate) && (
                                  <span className="text-xs bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded font-medium">Snart utløpt</span>
                                )}
                              </div>
                            )}
                          </div>
                        </button>

                        {/* Kvantitetsinput */}
                        {batch.confirmed && (
                          <div className="flex items-center gap-2 ml-2">
                            <span className="text-xs text-gray-500">Forv: {batch.currentQty}</span>
                            <input
                              type="number"
                              value={batch.countedQty}
                              onChange={(e) => updateCountedQty(batch.batchNo, e.target.value)}
                              className="w-20 px-2 py-1.5 border border-gray-300 rounded text-sm text-right focus:ring-1 focus:ring-orange-500 focus:border-orange-500"
                              min="0"
                              step="1"
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                        )}
                      </div>
                      {!batch.confirmed && (
                        <span className="text-xs text-red-600 font-medium ml-8">Mangler</span>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Nye batcher */}
              {newBatches.length > 0 && (
                <div className="space-y-1.5 mb-3">
                  <div className="text-xs font-medium text-orange-600 uppercase tracking-wider mb-2">
                    Nye batcher ({newBatches.length})
                  </div>
                  {newBatches.map((nb, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 rounded-lg border border-orange-300 bg-orange-50"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-5 h-5 rounded bg-orange-500 border-2 border-orange-500 flex items-center justify-center">
                          <Plus className="text-white" size={12} />
                        </div>
                        <div>
                          <span className="font-mono font-medium text-gray-900">{nb.batchNo}</span>
                          {nb.expiryDate && (
                            <span className="text-xs text-gray-500 ml-2">
                              <Calendar size={11} className="inline mr-0.5" />
                              {formatExpiryDate(nb.expiryDate)}
                            </span>
                          )}
                          <span className="text-xs text-gray-500 ml-2">Antall: {nb.quantity}</span>
                        </div>
                      </div>
                      <button
                        onClick={() => removeNewBatch(index)}
                        className="text-orange-400 hover:text-red-500 text-xs transition-colors"
                      >
                        Fjern
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {/* Legg til ny batch-seksjon */}
          <div className="mt-3 border-t pt-3">
            <button
              onClick={() => setShowAddNew(!showAddNew)}
              className="flex items-center gap-2 text-sm text-orange-600 hover:text-orange-800 font-medium transition-colors"
            >
              {showAddNew ? <EyeOff size={16} /> : <Eye size={16} />}
              {showAddNew ? 'Skjul' : 'Legg til ny batch'}
            </button>
            {showAddNew && (
              <div className="mt-3 space-y-2 bg-orange-50 rounded-lg p-3 border border-orange-200">
                {/* BatchNo input */}
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">
                    Batchnummer {itemType === 5 ? '(påkrevd)' : '(valgfritt — genereres fra utløpsdato)'}
                  </label>
                  <input
                    ref={addBatchInputRef}
                    type="text"
                    value={newBatchNo}
                    onChange={(e) => { setNewBatchNo(e.target.value); setError(null); }}
                    onFocus={(e) => e.stopPropagation()}
                    placeholder={itemType === 4 ? 'Auto fra utløpsdato...' : 'Skann batchnummer...'}
                    className="w-full px-3 py-2 border border-orange-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                    autoComplete="off"
                  />
                </div>

                {/* ExpiryDate input */}
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">
                    Utløpsdato {itemType === 4 ? '(påkrevd)' : '(valgfritt)'}
                  </label>
                  <input
                    type="date"
                    value={newExpiryDate}
                    onChange={(e) => { setNewExpiryDate(e.target.value); setError(null); }}
                    className="w-full px-3 py-2 border border-orange-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                  />
                </div>

                {/* Quantity input */}
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">Antall</label>
                  <input
                    type="number"
                    value={newQuantity}
                    onChange={(e) => { setNewQuantity(e.target.value); setError(null); }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        e.stopPropagation();
                        addNewBatch();
                      }
                    }}
                    placeholder="0"
                    min="0.01"
                    step="1"
                    className="w-full px-3 py-2 border border-orange-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                  />
                </div>

                <button
                  onClick={addNewBatch}
                  className="w-full px-3 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors flex items-center justify-center gap-1 text-sm font-medium"
                >
                  <Plus size={14} />
                  Legg til batch
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Footer med oppsummering */}
        <div className="p-4 border-t bg-gray-50">
          <div className="flex items-center gap-4 mb-3 text-xs">
            <span className="flex items-center gap-1 text-green-600">
              <CheckCircle size={12} /> Bekreftet: {confirmedCount}
            </span>
            {missingCount > 0 && (
              <span className="flex items-center gap-1 text-red-600">
                <AlertCircle size={12} /> Mangler: {missingCount}
              </span>
            )}
            {newCount > 0 && (
              <span className="flex items-center gap-1 text-orange-600">
                <Plus size={12} /> Nye: {newCount}
              </span>
            )}
          </div>

          <div className="flex items-center justify-between">
            <button
              onClick={onClose}
              className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Avbryt
            </button>
            <button
              onClick={handleConfirm}
              disabled={expectedBatches.length === 0 && newBatches.length === 0}
              className="px-5 py-2.5 text-sm font-medium rounded-lg transition-colors flex items-center gap-2 bg-orange-600 text-white hover:bg-orange-700 disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed"
            >
              <CheckCircle size={16} />
              Bekreft telling ({totalLines} linjer)
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
